import EastIcon from '@mui/icons-material/East';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box, Button, Link, Tooltip, Typography, useTheme } from '@mui/material';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import { useContext, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import PortalClosedBanner from '@/components/application_components/PortalClosedBanner';
import { Spinner } from '@/components/common/Spinner';
import DeadlineBanner from '@/components/dashboard_components/applicant_dashboard/DeadlineBanner';
import GrowthAwardErrorModal from '@/components/growth_award_components/GrowthAwardErrorModal';
import GrowthForm from '@/components/growth_award_components/GrowthForm';
import GrowthHeader from '@/components/growth_award_components/GrowthHeader';
import Header from '@/components/header/Header';
import {
  CurrentApplicationContext,
  CurrentApplicationProvider,
} from '@/Contexts/CurrentApplicationContext';
import { DashboardContext } from '@/Contexts/DashboardContext';
import { LocaleContext } from '@/Contexts/LocaleContext';
import { SelectedProgramContext } from '@/Contexts/SelectedProgramContext';
import { SnackbarContext } from '@/Contexts/SnackbarProvider';
import { UsersContext } from '@/Contexts/UsersContext';
import useApplication from '@/hooks/useApplication';
import useGrowthAward from '@/hooks/useGrowthAward';
import useQuestions from '@/hooks/useQuestions';
import { ApplicationTemplateQuestionProps } from '@/models/ApplicationTemplate';
import { ApplicationTypes } from '@/models/ApplicationType';
import { QuestionProps } from '@/models/Question';
import { UserType } from '@/models/User';
import { ApplicationStatus } from '@/utils/constants';
import { logError } from '@/utils/errorHandlerUtil';
import { isGrowthAwardPortalClosed, sumActivityPoints } from '@/utils/growthAwardUtil';
import { QuestionReviewProps } from '@/models/QuestionReview';

const AccordionWrap = ({
  questions,
  activityAnswers,
  categories,
  handleChange,
  expanded,
  submitOnBlur,
  updateAfterDelete,
  userType,
  appStatus,
  applicationId,
}: {
  questions: QuestionProps[] | null | undefined;
  activityAnswers: any;
  categories: any;
  handleChange: any;
  expanded: any;
  submitOnBlur: (currentQuestions: any, currentAnswers: any) => Promise<void>;
  updateAfterDelete: any;
  userType: any;
  appStatus: ApplicationStatus | undefined;
  applicationId: number;
}) => {
  const theme = useTheme();
  const { deleteGrowthAwardResponse } = useGrowthAward();

  async function deleteActivity(activity: any) {
    await deleteGrowthAwardResponse(activity.activityId, applicationId).then((resp: any) => {
      if (resp.includes('success')) {
        updateAfterDelete();
      }
    });
  }

  return activityAnswers.length > 0 ? (
    <Box
      display='flex'
      flexDirection='column'
      width='100%'
      alignItems='center'
      textAlign='center'
      border={`1px solid ${theme.palette.sandShade3.main}`}
      borderRadius={'4px'}
      paddingLeft={3}
      paddingRight={3}
      paddingBottom={3}
      sx={{ backgroundColor: theme.palette.sandTint.main }}
    >
      <>
        <Typography variant='h4' style={{ marginBottom: 0 }}>
          Activities Applied For
        </Typography>
        <p style={{ marginTop: 0 }}>
          Edit and view answers for activities you&apos;ve already answered below.
        </p>
        {activityAnswers.map((activity: any, index: any) => {
          const ca = categories?.find((cat: any) => cat.categoryId == activity.categoryId);

          const su = ca?.subcategories.find((s: any) => s.subCategoryId == activity.subCategoryId);

          const ac = su?.activities.find((x: any) => x.activityId == activity.activityId);

          return (
            <Box key={`${activity.activityName}-box`} width='100%'>
              <Accordion
                key={activity.activityName}
                expanded={expanded === `panel${index}`}
                onChange={handleChange(`panel${index}`, activity)}
                sx={{
                  border: 'none',
                  boxShadow: 'none',
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls={'panel' + { index } + 'd - content'}
                  id={'panel' + { index } + 'd-header'}
                >
                  <Box
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      flexGrow: 1,
                      textAlign: 'left',
                      paddingRight: '10px',
                    }}
                  >
                    <Typography
                      style={{
                        flexBasis: '30px',
                        flexGrow: '0',
                        textAlign: 'right',
                      }}
                    >
                      {ca?.category.split('.')[0] + '.'}
                    </Typography>
                    <Typography
                      style={{
                        flexBasis: 'auto',
                        padding: '0px 5px',
                      }}
                    >
                      {ca?.category.split('.')[1]}
                    </Typography>
                    <Typography
                      style={{
                        flexBasis: '40px',
                        flexGrow: '0',
                        textAlign: 'center',
                      }}
                    >
                      <EastIcon fontSize='small' />
                    </Typography>
                    <Typography
                      style={{
                        flexBasis: 'auto',
                        paddingRight: '5px',
                      }}
                    >
                      {su?.subCategory}
                    </Typography>
                    <Typography
                      style={{
                        flexBasis: '40px',
                        flexGrow: '0',
                        textAlign: 'center',
                      }}
                    >
                      <EastIcon fontSize='small' />
                    </Typography>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        flexGrow: 1,
                        flexBasis: 'auto',
                        justifyContent: 'space-between',
                      }}
                    >
                      <Typography>{`${ac?.activityName.split('.')[0]}.`}</Typography>
                      <Typography
                        style={{
                          flexGrow: 1,
                          paddingLeft: '5px',
                        }}
                      >
                        {`${ac?.activityName.split('.')[1]}`}
                      </Typography>
                      <Typography
                        color='textSecondary'
                        style={{
                          float: 'right',
                        }}
                      >
                        {`${ac?.activityPoints} points`}
                      </Typography>
                    </Box>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  {questions && (
                    <GrowthForm
                      questions={questions.filter(
                        (q: QuestionProps) => q.activityId === activity.activityId
                      )}
                      postInputs={submitOnBlur}
                      userType={userType}
                      appStatus={appStatus}
                      activity={activity}
                      deleteActivity={deleteActivity}
                    />
                  )}
                </AccordionDetails>
              </Accordion>
            </Box>
          );
        })}
      </>
    </Box>
  ) : (
    <></>
  );
};

function GrowthAwardContent() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { appid: paramAppId } = useParams();

  const { currentApplication, canEdit, answers, allComments, updateMakingApiRequest, partnerInfo } =
    useContext(CurrentApplicationContext);

  const { updateApplicationStatus } = useContext(DashboardContext);

  const { portalOpen, selectedProgramId } = useContext(SelectedProgramContext);

  const { userType } = useContext(UsersContext);
  const { setAlert } = useContext(SnackbarContext);

  const [subHeader, setSubHeader] = useState<any>(null);
  const [totalPoints, setTotalPoints] = useState<number>(0);

  const {
    getGrowthAward,
    getGrowthAwardResponses,
    postGrowthAwardAnswer,
    postGrowthAwardResponses,
  } = useGrowthAward();

  const { postSubmit, postApplicationStatus, postResubmit } = useApplication();

  const { getQuestions } = useQuestions();
  const { locale, translatedText } = useContext(LocaleContext);

  const [questions, setQuestions] = useState<QuestionProps[] | null>();
  const [categories, setCategories] = useState<any>([]);
  const [activities, setActivities] = useState<any>([]);
  const [subCats, setSubCats] = useState<any>([]);
  const [selectedCategory, setSelectedCategory] = useState<any>(null);
  const [selectedSubCat, setSelectedSubCat] = useState<any>(null);
  const [selectedAct, setSelectedAct] = useState<any>(null);
  const [activityQuestions, setActivityQuestions] = useState<QuestionProps[] | null>([]);
  const [activityAnswers, setActivityAnswers] = useState<any>([]);
  const divRef = useRef<null | HTMLDivElement>(null);
  const [expanded, setExpanded] = useState<string | false>(false);
  const [errorModalOpen, setErrorModalOpen] = useState<boolean>(false);
  const [errorMessages, setErrorMessages] = useState<string[]>([]);

  const [submitButtonText, setSubmitButtonText] = useState<string>(
    translatedText.applicationBody.submitButton
  );
  const [sendBackButtonText, setSendBackButtonText] = useState<string>(
    translatedText.applicationBody.sendBackToApplicantWithComments
  );
  const [approveButtonText, setApproveButtonText] = useState<string>(
    translatedText.applicationBody.approveApplicationButton
  );

  const [loading, setLoading] = useState<boolean>(true);
  const [submitClicked, setSubmitClicked] = useState<boolean>(false);

  const portalClosed = isGrowthAwardPortalClosed(portalOpen, canEdit);

  const handleChange =
    (panel: string, activity: any) => (event: React.SyntheticEvent, isExpanded: boolean) => {
      setSelectedAct(activity);
      setSelectedSubCat(null);
      setExpanded(isExpanded ? panel : false);
    };

  const [growthAwardDataFetched, setGrowthAwardDataFetched] = useState<boolean>(false);

  useEffect(() => {
    if (paramAppId && !growthAwardDataFetched) {
      setGrowthAwardDataFetched(true);

      getGrowthAward()
        .then((data: any) => {
          setCategories(data);
        })
        .catch((error) => {
          logError('Error fetching growth award categories.', error);
          setAlert({
            message: translatedText.common.messageGrowthAwardCategoriesFetchError,
            severity: 'error',
          });
          navigate('/error');
        });
    }
  }, [getGrowthAward, growthAwardDataFetched, navigate, paramAppId, setAlert]);

  useEffect(() => {
    if (
      currentApplication.status != null &&
      currentApplication.status > ApplicationStatus.InProgress &&
      currentApplication.status !== ApplicationStatus.InRevision
    ) {
      setSubmitButtonText(translatedText.common.statusApplicationSubmitted);
    } else if (currentApplication.status === ApplicationStatus.InRevision) {
      setSubmitButtonText(translatedText.applicationBody.resubmitButton);
    }
  }, [
    currentApplication,
    translatedText.applicationBody.resubmitButton,
    translatedText.common.statusApplicationSubmitted,
  ]);

  useEffect(() => {
    if (locale != null && currentApplication.applicationTemplateId != null && questions == null) {
      const fetchData = async () => {
        const templateQuestionsProp: ApplicationTemplateQuestionProps | null = await getQuestions(
          currentApplication.applicationTemplateId,
          locale
        );

        if (templateQuestionsProp == null) {
          setAlert({
            message: `No questions were loaded for application ${currentApplication.id}`,
            severity: 'error',
          });

          return;
        }

        const templateQuestions = templateQuestionsProp.standards[0]?.questions;

        setQuestions(templateQuestions);

        if (paramAppId != null) {
          const info = await getGrowthAwardResponses(Number(paramAppId));
          if (info != null) {
            const currentActivities = info.activities ?? [];
            setActivityAnswers(currentActivities);
            setTotalPoints(sumActivityPoints(currentActivities));
          }
        }
      };

      void fetchData();
    }
  }, [
    currentApplication.applicationTemplateId,
    currentApplication.id,
    getGrowthAwardResponses,
    getQuestions,
    locale,
    paramAppId,
    questions,
    setAlert,
  ]);

  useEffect(() => {
    if (questions != null && questions.length > 0) {
      const subheader = questions.find((q: QuestionProps) => q.questionType === 'subheader');
      if (subheader != null) {
        setSubHeader(subheader);
      }
    }
  }, [questions]);

  useEffect(() => {
    if (
      currentApplication != null &&
      activityAnswers != null &&
      questions != null &&
      selectedProgramId != null &&
      partnerInfo != null &&
      answers != null
    ) {
      setLoading(false);
    }
  }, [answers, activityAnswers, currentApplication, partnerInfo, questions, selectedProgramId]);

  const updateAfterDelete = async () => {
    setAlert({
      message: 'Activity Deleted',
      severity: 'success',
    });

    setExpanded(false);

    if (currentApplication.id != null) {
      const info = await getGrowthAwardResponses(currentApplication.id);
      if (info != null) {
        const currentActivities = info.activities ?? [];

        setActivityAnswers(currentActivities);
        setTotalPoints(sumActivityPoints(currentActivities));
      }
    }
  };

  const submitOnBlur = async (currentQuestions: any, currentAnswers: any) => {
    updateMakingApiRequest(true);

    const promises: any = [];
    currentQuestions.forEach((question: any) => {
      const questionResponse = currentAnswers[question.id];

      if (!questionResponse) return;

      if (question.questionType == 'textbox') {
        promises.push(
          postGrowthAwardAnswer(
            Number(currentApplication.id),
            question.id,
            questionResponse.responseString,
            'string'
          )
        );
      }
    });

    await Promise.all(promises).then(() => {
      const postArray: any = [];

      activityAnswers.forEach((activity: any) => {
        postArray.push({
          applicationId: currentApplication.id,
          growthAwardActivityId: activity.activityId,
        });
      });
      const existingObj = postArray.find(
        (item: any) => item.growthAwardActivityId === selectedAct?.activityId
      );

      if (!existingObj && currentApplication.id) {
        postArray.push({
          applicationId: currentApplication.id,
          growthAwardActivityId: selectedAct?.activityId,
        });
        postGrowthAwardResponses(postArray)
          .then(() => {
            return getGrowthAwardResponses(currentApplication.id);
          })
          .then((info: any) => {
            if (info) {
              const currentActivities = info.activities ?? [];
              setActivityAnswers(currentActivities);
              setTotalPoints(sumActivityPoints(currentActivities));
            }

            clearGrowthForm();
            updateMakingApiRequest(false);
          })
          .catch((error) => {
            logError(
              `Error posting growth award responses for application ${currentApplication.id}.`,
              error
            );
            updateMakingApiRequest(false);
          });
      } else {
        updateMakingApiRequest(false);
      }

      setAlert({
        message: 'Activity Answered and Total Points Updated!',
        severity: 'success',
      });
      setExpanded(false);
    });
  };

  useEffect(() => {
    if (selectedCategory) {
      setSubCats([]);
      setActivities([]);
      setSelectedAct(null);
      setSelectedSubCat(null);

      const subs = categories.find(
        (subcategory: any) => subcategory.categoryId === selectedCategory
      );

      setSubCats(subs.subcategories);
    }
  }, [selectedCategory, categories]);

  useEffect(() => {
    if (selectedSubCat) {
      setActivities([]);
      setSelectedAct(null);

      const selectedSubCatActivity = subCats.find(
        (activity: any) => activity.subCategoryId === selectedSubCat
      );

      setActivities(selectedSubCatActivity?.activities);
    }
  }, [selectedSubCat, subCats]);

  useEffect(() => {
    if (selectedAct && questions) {
      const qs = questions.filter((q: QuestionProps) => q.activityId === selectedAct.activityId);

      setActivityQuestions(qs);
    }
  }, [selectedAct, questions]);

  function clearGrowthForm() {
    setSubCats([]);
    setActivities([]);
    setSelectedAct(null);
    setSelectedSubCat(null);
    const subs = categories.find((subcategory: any) => subcategory.categoryId === selectedCategory);
    setSubCats(subs.subcategories);
  }

  return (
    <Box display='flex' flexDirection='column' height='100vh' width='100vw'>
      <DeadlineBanner />
      <Header />
      {loading ? (
        <Box position='relative' height='250px'>
          <Spinner />
        </Box>
      ) : (
        <Box
          sx={{
            overflowX: 'hidden',
            scrollbarGutter: 'stable',
            display: 'flex',
            flexDirection: 'column',
            gap: '24px',
            paddingBottom: '24px',
          }}
        >
          <GrowthHeader name={partnerInfo?.partnerName ?? null} />
          <Box display='flex' flex={1} justifyContent='center' className='wrap' ref={divRef}>
            <Box
              display='flex'
              flexDirection='column'
              flexGrow={1}
              gap='10px'
              minWidth='500px'
              maxWidth='1080px'
            >
              <Box
                display='flex'
                flexDirection='column'
                alignItems='center'
                justifyContent='center'
              >
                <Typography variant='h2'>Growth Award {subHeader?.questionText}</Typography>
                <div
                  style={{
                    textAlign: 'justify',
                    width: '80%',
                  }}
                >
                  <ul>
                    <li>
                      Community must have been certified as a Tree City USA for at least one
                      previous year and submitted a recertification application this year.
                    </li>
                    <li>
                      Community must earn 10 points according to Growth Award Eligible Activities –
                      which were new or significantly improved compared to the previous year.
                    </li>
                    <li>
                      Attach documentation for each eligible activity listed below. Appropriate
                      documentation includes narrative descriptions, copies of documents, copies of
                      printed brochures, and newspaper clippings.
                    </li>
                  </ul>
                </div>

                <Typography margin={'5px'} variant='h3'>
                  Total Points: {totalPoints}
                </Typography>
                {portalClosed && (
                  <PortalClosedBanner applicationTypeId={ApplicationTypes.GrowthAward} />
                )}
                <Box display='flex'>
                  {((totalPoints >= 10 && userType == UserType.Applicant) ||
                    (currentApplication.status &&
                      [ApplicationStatus.InProgress, ApplicationStatus.InRevision].includes(
                        currentApplication.status
                      ) &&
                      userType != UserType.Applicant)) && (
                    <Tooltip title={translatedText.common.tooltipSubmitGrowthAward}>
                      <div
                        style={{
                          flexGrow: 1,
                          margin: '20px',
                        }}
                      >
                        <Button
                          disabled={submitClicked || portalClosed || totalPoints < 10}
                          onClick={() => {
                            if (currentApplication.status == ApplicationStatus.InProgress) {
                              submitApp();
                            } else if (currentApplication.status == ApplicationStatus.InRevision) {
                              resubmitApp();
                            }
                          }}
                          variant='primary'
                          size='large'
                        >
                          {submitButtonText}
                        </Button>
                      </div>
                    </Tooltip>
                  )}
                  {currentApplication.status &&
                    ((userType == UserType.RegionalCoordinator &&
                      currentApplication.status <= ApplicationStatus.PendingRegionalReview) ||
                      (userType == UserType.StateCoordinator &&
                        currentApplication.status <= ApplicationStatus.PendingStateReview) ||
                      (userType == UserType.Adf &&
                        currentApplication.status <= ApplicationStatus.PendingAdfReview)) && (
                      <>
                        <Tooltip
                          title={translatedText.applicationBody.tooltipSendBackRequiresComment}
                        >
                          <div
                            style={{
                              flexGrow: 1,
                              margin: '20px',
                            }}
                          >
                            <Button
                              disabled={
                                submitClicked ||
                                portalOpen == null ||
                                portalOpen[ApplicationTypes.GrowthAward] === false ||
                                [
                                  ApplicationStatus.InProgress,
                                  ApplicationStatus.InRevision,
                                ].includes(currentApplication.status)
                              }
                              onClick={sendBacktoApplicant}
                              variant='secondary'
                              size='large'
                            >
                              {sendBackButtonText}
                            </Button>
                          </div>
                        </Tooltip>
                        <Tooltip
                          title={
                            translatedText.applicationBody.tooltipApproveSendsToNextReviewStage
                          }
                        >
                          <div
                            style={{
                              flexGrow: 1,
                              margin: '20px',
                            }}
                          >
                            <Button
                              onClick={approveApp}
                              variant='primary'
                              size='large'
                              disabled={submitClicked || portalClosed}
                            >
                              {approveButtonText}
                            </Button>
                          </div>
                        </Tooltip>
                      </>
                    )}
                </Box>
              </Box>
              <Box
                display='flex'
                flexDirection='column'
                alignItems='center'
                padding={5}
                sx={{
                  backgroundColor: theme.palette.sandShade1.main,
                }}
                gap={1}
              >
                <div
                  style={{
                    textAlign: 'justify',
                    width: '80%',
                  }}
                  dangerouslySetInnerHTML={{
                    __html: subHeader?.questionNote,
                  }}
                ></div>
                <span style={{ marginBottom: '30px' }}>
                  To view all qualifying Growth Award activities, click{' '}
                  <Link
                    target='_blank'
                    href='https://www.arborday.org/programs/treecityusa/growth-award/'
                  >
                    here
                  </Link>
                  .
                </span>
                {((userType == UserType.Applicant &&
                  currentApplication.status &&
                  [ApplicationStatus.InProgress, ApplicationStatus.InRevision].includes(
                    currentApplication.status
                  )) ||
                  userType != UserType.Applicant) && (
                  <>
                    <FormControl sx={{ m: 1, width: 320 }}>
                      <InputLabel id='ga-category-inputlbl' style={{ top: '-8px' }}>
                        Categories
                      </InputLabel>
                      <Select
                        labelId='ga-category-selectlbl'
                        id='ga-category-select'
                        value={selectedCategory}
                        label='Categories'
                        disabled={!canEdit}
                        onChange={(e: any) => {
                          setSelectedCategory(e.target.value);
                        }}
                        size='small'
                      >
                        {categories.map((x: any) => {
                          return (
                            <MenuItem key={x.categoryId} value={x.categoryId}>
                              {x.category}
                            </MenuItem>
                          );
                        })}
                      </Select>
                    </FormControl>
                    {subCats && subCats.length > 0 && (
                      <FormControl sx={{ m: 1, width: 320 }}>
                        <InputLabel id='ga-subcategory-inputlbl' style={{ top: '-8px' }}>
                          Subcategories
                        </InputLabel>
                        <Select
                          labelId='ga-subcategory-selectlbl'
                          id='ga-subcategory-select'
                          value={selectedSubCat}
                          label='Subcategories'
                          disabled={!canEdit}
                          onChange={(e: any) => {
                            setSelectedSubCat(e.target.value);
                          }}
                          size='small'
                        >
                          {subCats.map((x: any) => {
                            return (
                              <MenuItem key={x.subCategoryId} value={x.subCategoryId}>
                                {x.subCategory}
                              </MenuItem>
                            );
                          })}
                        </Select>
                      </FormControl>
                    )}

                    {selectedSubCat && activities && (
                      <Box display='flex' flexDirection='column' alignItems={'center'}>
                        <FormControl
                          sx={{
                            m: 1,
                            width: 320,
                          }}
                        >
                          <InputLabel id='ga-activity-inputlbl' style={{ top: '-8px' }}>
                            Activities
                          </InputLabel>
                          <Select
                            labelId='ga-activity-selectlbl'
                            id='ga-activity-select'
                            value={selectedAct}
                            label='Activities'
                            renderValue={(value: any) => {
                              return value.activityName;
                            }}
                            disabled={!canEdit}
                            onChange={(e: any) => {
                              setSelectedAct(e.target.value);
                            }}
                            size='small'
                          >
                            {activities.map((x: any) => {
                              return (
                                <MenuItem
                                  key={x.activityId}
                                  value={x}
                                  sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    width: '100%',
                                    gap: '24px',
                                  }}
                                >
                                  <Typography>{x.activityName}</Typography>
                                  <Typography color='textSecondary'>{`${x.activityPoints} points`}</Typography>
                                </MenuItem>
                              );
                            })}
                          </Select>
                        </FormControl>
                      </Box>
                    )}
                  </>
                )}
                <Box
                  display='flex'
                  flexDirection='column'
                  alignItems='center'
                  minHeight='700px'
                  width='80%'
                  gap='15px'
                  marginTop='20px'
                >
                  {selectedAct != null && selectedSubCat != null ? (
                    <>
                      <Typography variant='h3'>
                        {selectedAct.activityName}: {selectedAct.activityPoints} Points
                      </Typography>
                      <Typography>
                        <Box
                          color={theme.palette.text.secondary}
                          dangerouslySetInnerHTML={{
                            __html: selectedAct.activityDescription,
                          }}
                        ></Box>
                      </Typography>
                      {activityQuestions != null && activityQuestions.length > 0 ? (
                        <GrowthForm
                          questions={activityQuestions}
                          postInputs={submitOnBlur}
                          userType={userType}
                          appStatus={currentApplication.status}
                        />
                      ) : (
                        <div />
                      )}
                    </>
                  ) : (
                    <Box></Box>
                  )}
                  {activityAnswers != null && currentApplication.id != null && (
                    <AccordionWrap
                      questions={questions}
                      activityAnswers={activityAnswers}
                      categories={categories}
                      handleChange={handleChange}
                      expanded={expanded}
                      submitOnBlur={submitOnBlur}
                      updateAfterDelete={updateAfterDelete}
                      userType={userType}
                      appStatus={currentApplication.status}
                      applicationId={currentApplication.id}
                    />
                  )}
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      )}

      {errorModalOpen && (
        <GrowthAwardErrorModal
          open={errorModalOpen}
          onClose={() => setErrorModalOpen(false)}
          errorMessages={errorMessages}
        />
      )}
    </Box>
  );

  function submitApp() {
    setAlert({
      message: translatedText.common.messageGrowthAwardSubmitting,
      severity: 'info',
    });
    setSubmitButtonText(translatedText.applicationBody.submitMessage);
    setSubmitClicked(true);

    if (currentApplication.id) {
      postSubmit(currentApplication.id, null)
        .then(async (updatedApplicationStatus: ApplicationStatus) => {
          setAlert({
            message: translatedText.common.messageGrowthAwardSubmitted,
            severity: 'success',
          });
          setSubmitButtonText(translatedText.common.statusApplicationSubmitted);
          updateApplicationStatus(updatedApplicationStatus, currentApplication.id);
          setTimeout(() => {
            navigate('/submit-confirmation', {
              state: {
                applicationId: currentApplication.id,
              },
            });
            setSubmitClicked(false);
          }, 1000);
        })
        .catch((error) => {
          setAlert({
            message: translatedText.common.messageGrowthAwardSubmitProblem,
            severity: 'error',
          });
          setSubmitButtonText(translatedText.applicationBody.submitButton);
          setSubmitClicked(false);
          logError(
            `Error submitting growth award for application ${currentApplication.id}.`,
            error
          );
        });
    }
  }

  function resubmitApp() {
    setAlert({
      message: translatedText.common.messageGrowthAwardSubmitting,
      severity: 'info',
    });

    setSubmitButtonText(translatedText.applicationBody.submitMessage);
    setSubmitClicked(true);

    if (currentApplication.id) {
      postResubmit(currentApplication.id)
        .then((updatedApplicationStatus: ApplicationStatus) => {
          setAlert({
            message: translatedText.common.messageGrowthAwardSubmitted,
            severity: 'success',
          });

          setSubmitButtonText(translatedText.common.statusApplicationSubmitted);
          updateApplicationStatus(updatedApplicationStatus, currentApplication.id);
          setTimeout(() => {
            navigate('/submit-confirmation', {
              state: {
                applicationId: currentApplication.id,
              },
            });
            setSubmitClicked(false);
          }, 1000);
        })
        .catch((error) => {
          setAlert({
            message: translatedText.common.messageGrowthAwardSubmitProblem,
            severity: 'error',
          });
          setSubmitButtonText(translatedText.applicationBody.resubmitButton);
          setSubmitClicked(false);
          logError(
            `Error resubmitting growth award for application ${currentApplication.id}.`,
            error
          );
        });
    }
  }

  function sendBacktoApplicant() {
    const questionHasComments: boolean = allComments != null && allComments.length > 0;

    const allCommentsResolved: boolean =
      questionHasComments != null &&
      allComments != null &&
      allComments.every((review: QuestionReviewProps) => review.resolved);

    if (!questionHasComments || allCommentsResolved) {
      setAlert({
        message: translatedText.common.messageGrowthAwardCommentRequired,
        severity: 'warning',
      });
      return;
    }

    setAlert({
      message: translatedText.common.messageGrowthAwardSendingBack,
      severity: 'info',
    });
    setSendBackButtonText(translatedText.common.statusReturning);
    setSubmitClicked(true);

    postApplicationStatus(currentApplication.id, true)
      .then(async (updatedApplicationStatus: ApplicationStatus) => {
        setAlert({
          message: translatedText.common.messageGrowthAwardReturnSuccess,
          severity: 'success',
        });
        setSendBackButtonText(translatedText.common.statusApplicationReturned);
        updateApplicationStatus(updatedApplicationStatus, currentApplication.id);
        setTimeout(() => {
          navigate('/dashboard');
        }, 1000);
      })
      .catch((error) => {
        setAlert({
          message: translatedText.common.messageGrowthAwardSendBackProblem,
          severity: 'error',
        });
        setSendBackButtonText(translatedText.applicationBody.sendBackToApplicantWithComments);
        logError(
          `Error sending back growth award for application ${currentApplication.id}.`,
          error
        );
      })
      .finally(() => {
        setSubmitClicked(false);
      });
  }

  function approveApp() {
    if (currentApplication.id == null) return;

    setAlert({
      message: translatedText.common.messageGrowthAwardApproving,
      severity: 'info',
    });
    setApproveButtonText(translatedText.common.statusApproving);
    setSubmitClicked(true);

    postApplicationStatus(currentApplication.id, false)
      .then(async (updatedApplicationStatus: ApplicationStatus | null) => {
        if (updatedApplicationStatus) {
          setAlert({
            message: translatedText.common.messageGrowthAwardApprovalSuccess,
            severity: 'success',
          });
          setApproveButtonText(translatedText.common.statusApplicationApproved);

          updateApplicationStatus(updatedApplicationStatus, currentApplication.id);
          setTimeout(() => {
            navigate('/dashboard');
            setSubmitClicked(false);
          }, 1000);
        } else {
          setErrorModalOpen(true);
          setErrorMessages([translatedText.common.messageGrowthAwardApproveProblem]);
          setAlert({
            message: translatedText.common.messageGrowthAwardApproveProblem,
            severity: 'error',
          });
          setApproveButtonText(translatedText.applicationBody.approveApplicationButton);
          setSubmitClicked(false);
        }
      })
      .catch((error) => {
        setErrorModalOpen(true);
        setErrorMessages([translatedText.common.messageGrowthAwardApproveProblem]);
        setAlert({
          message: translatedText.common.messageGrowthAwardApproveProblem,
          severity: 'error',
        });
        setApproveButtonText(translatedText.applicationBody.approveApplicationButton);
        setSubmitClicked(false);
        logError(`Error approving growth award for application ${currentApplication.id}.`, error);
      });
  }
}

export default function GrowthAward() {
  const { appid } = useParams();

  return (
    <CurrentApplicationProvider key={appid} appid={appid}>
      <GrowthAwardContent />
    </CurrentApplicationProvider>
  );
}
